import { useState, useEffect, useCallback, useRef } from 'react'
import jsQR from 'jsqr'
import { listMfaDevices, createMfaDevice, deleteMfaDevice, generateMfaCode } from '../services/api'

function MfaDeviceManager({ onBack }) {
  const [devices, setDevices] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [creating, setCreating] = useState(false)
  const [scanError, setScanError] = useState(null)
  const [scannedUri, setScannedUri] = useState(null)
  const [deviceName, setDeviceName] = useState('')
  const [showManualInput, setShowManualInput] = useState(false)
  const [manualUri, setManualUri] = useState('')
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [generatedCode, setGeneratedCode] = useState(null)
  const [generating, setGenerating] = useState(null)
  const [countdown, setCountdown] = useState(null)
  const countdownRef = useRef(null)
  const fileInputRef = useRef(null)

  const loadDevices = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await listMfaDevices()
      setDevices(response.devices || [])
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadDevices()
  }, [loadDevices])

  // Countdown timer for TOTP codes
  useEffect(() => {
    if (generatedCode && generatedCode.type === 'totp' && countdown > 0) {
      countdownRef.current = setTimeout(() => {
        setCountdown((prev) => prev - 1)
      }, 1000)
    }
    if (countdown === 0) {
      setGeneratedCode(null)
      setCountdown(null)
    }
    return () => {
      if (countdownRef.current) clearTimeout(countdownRef.current)
    }
  }, [countdown, generatedCode])

  const decodeQrFromFile = (file) => {
    setScanError(null)
    setScannedUri(null)

    const reader = new FileReader()
    reader.onload = (e) => {
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        canvas.width = img.width
        canvas.height = img.height
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0)
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
        const code = jsQR(imageData.data, imageData.width, imageData.height)

        if (code && code.data.startsWith('otpauth://')) {
          setScannedUri(code.data)
          setScanError(null)
        } else if (code) {
          setScanError('QR code does not contain an otpauth:// URI')
        } else {
          setScanError('Could not detect a QR code in the image')
        }
      }
      img.onerror = () => {
        setScanError('Could not load the image file')
      }
      img.src = e.target.result
    }
    reader.readAsDataURL(file)
  }

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0]
    if (file) decodeQrFromFile(file)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    const file = e.dataTransfer.files?.[0]
    if (file) decodeQrFromFile(file)
  }

  const handleCreate = async (e) => {
    e.preventDefault()
    const uri = scannedUri || manualUri
    if (!uri) return

    try {
      setCreating(true)
      setError(null)
      await createMfaDevice(uri, deviceName)
      closeCreateModal()
      await loadDevices()
    } catch (err) {
      setError(err.message)
    } finally {
      setCreating(false)
    }
  }

  const closeCreateModal = () => {
    setShowCreateModal(false)
    setScannedUri(null)
    setScanError(null)
    setDeviceName('')
    setManualUri('')
    setShowManualInput(false)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleDelete = async (deviceId) => {
    try {
      setDeleting(true)
      setError(null)
      await deleteMfaDevice(deviceId)
      setDeleteConfirm(null)
      if (generatedCode && generatedCode.deviceId === deviceId) {
        setGeneratedCode(null)
        setCountdown(null)
      }
      await loadDevices()
    } catch (err) {
      setError(err.message)
    } finally {
      setDeleting(false)
    }
  }

  const handleGenerateCode = async (device) => {
    try {
      setGenerating(device.device_id)
      setError(null)
      const response = await generateMfaCode(device.device_id)
      setGeneratedCode({ deviceId: device.device_id, ...response })
      if (response.type === 'totp' && response.expires_in) {
        setCountdown(response.expires_in)
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setGenerating(null)
    }
  }

  const handleCopyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code)
    } catch {
      // Fallback: select text for manual copy
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Never'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Parse issuer/account from a scanned URI for preview
  const parseOtpauthPreview = (uri) => {
    try {
      const url = new URL(uri)
      const issuer = url.searchParams.get('issuer') || ''
      const label = decodeURIComponent(url.pathname.replace(/^\/\/[^/]+\//, ''))
      return { issuer, label }
    } catch {
      return { issuer: '', label: '' }
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Addresses
          </button>
          <h2 className="mt-2 text-xl font-semibold text-gray-900">MFA Devices</h2>
          <p className="text-sm text-gray-500">Manage your TOTP/HOTP authenticator devices</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          Add Device
        </button>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8">
          <svg className="h-8 w-8 animate-spin text-gray-400" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        </div>
      ) : devices.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">No MFA Devices</h3>
          <p className="mt-2 text-sm text-gray-500">
            Upload a QR code image from a service to start generating codes.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {devices.map((device) => (
            <div
              key={device.device_id}
              className="rounded-lg border border-gray-200 bg-white p-4"
            >
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                  <h3 className="font-medium text-gray-900">{device.name}</h3>
                  <div className="mt-1 space-y-1 text-sm text-gray-500">
                    <p>
                      <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                        {device.type.toUpperCase()}
                      </span>
                      {device.issuer && (
                        <span className="ml-2">Issuer: {device.issuer}</span>
                      )}
                      {device.account && (
                        <span className="ml-2">Account: {device.account}</span>
                      )}
                    </p>
                    <p>
                      {device.algorithm} / {device.digits} digits
                      {device.period && ` / ${device.period}s`}
                    </p>
                    <p>Created: {formatDate(device.created_at)}</p>
                    <p>Last used: {formatDate(device.last_used_at)}</p>
                  </div>

                  {/* Generated code display */}
                  {generatedCode && generatedCode.deviceId === device.device_id && (
                    <div className="mt-3 flex items-center gap-3">
                      <code className="rounded-md bg-gray-100 px-3 py-1.5 text-lg font-mono font-bold tracking-wider text-gray-900">
                        {generatedCode.code}
                      </code>
                      <button
                        onClick={() => handleCopyCode(generatedCode.code)}
                        className="rounded-md bg-gray-100 px-2 py-1.5 text-sm text-gray-600 hover:bg-gray-200"
                        title="Copy code"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                      {generatedCode.type === 'totp' && countdown !== null && (
                        <span className="text-sm text-gray-500">
                          Expires in {countdown}s
                        </span>
                      )}
                      {generatedCode.type === 'hotp' && generatedCode.counter !== undefined && (
                        <span className="text-sm text-gray-500">
                          Counter: {generatedCode.counter}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="ml-4 flex shrink-0 gap-2">
                  <button
                    onClick={() => handleGenerateCode(device)}
                    disabled={generating === device.device_id}
                    className="rounded-md bg-green-50 px-3 py-1.5 text-sm font-medium text-green-700 hover:bg-green-100 disabled:opacity-50"
                  >
                    {generating === device.device_id ? 'Generating...' : 'Generate Code'}
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(device)}
                    className="rounded-md bg-red-50 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-100"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Device Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-lg font-medium text-gray-900">Add MFA Device</h3>
            <form onSubmit={handleCreate} className="mt-4 space-y-4">

              {/* QR code upload area */}
              {!scannedUri && !showManualInput && (
                <div
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className="cursor-pointer rounded-lg border-2 border-dashed border-gray-300 p-8 text-center hover:border-blue-400 hover:bg-blue-50/50"
                >
                  <svg className="mx-auto h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="mt-2 text-sm font-medium text-gray-700">
                    Upload QR code image
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    Drop an image here or click to select
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>
              )}

              {/* Scan error */}
              {scanError && (
                <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
                  {scanError}
                </div>
              )}

              {/* Scanned URI preview */}
              {scannedUri && (
                <div className="rounded-md bg-green-50 p-3">
                  <div className="flex items-center gap-2">
                    <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm font-medium text-green-800">QR code scanned</span>
                  </div>
                  <p className="mt-1 text-sm text-green-700">
                    {(() => {
                      const { issuer, label } = parseOtpauthPreview(scannedUri)
                      return issuer ? `${issuer} — ${label}` : label || scannedUri
                    })()}
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setScannedUri(null)
                      setScanError(null)
                      if (fileInputRef.current) fileInputRef.current.value = ''
                    }}
                    className="mt-1 text-xs text-green-600 underline hover:text-green-800"
                  >
                    Choose a different image
                  </button>
                </div>
              )}

              {/* Manual URI fallback */}
              {!scannedUri && !showManualInput && (
                <button
                  type="button"
                  onClick={() => setShowManualInput(true)}
                  className="text-sm text-gray-500 underline hover:text-gray-700"
                >
                  Or paste an otpauth:// URI manually
                </button>
              )}

              {showManualInput && !scannedUri && (
                <div>
                  <label htmlFor="manual-uri" className="block text-sm font-medium text-gray-700">
                    OTPAuth URI
                  </label>
                  <input
                    type="text"
                    id="manual-uri"
                    value={manualUri}
                    onChange={(e) => setManualUri(e.target.value)}
                    placeholder="otpauth://totp/Issuer:account?secret=...&issuer=..."
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              )}

              {/* Optional device name */}
              {(scannedUri || showManualInput) && (
                <div>
                  <label htmlFor="device-name" className="block text-sm font-medium text-gray-700">
                    Device Name (optional)
                  </label>
                  <input
                    type="text"
                    id="device-name"
                    value={deviceName}
                    onChange={(e) => setDeviceName(e.target.value)}
                    placeholder="e.g., GitHub, AWS Console"
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              )}

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={closeCreateModal}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating || (!scannedUri && !manualUri)}
                  className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {creating ? 'Adding...' : 'Add Device'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-lg font-medium text-gray-900">Delete MFA Device</h3>
            <p className="mt-2 text-sm text-gray-500">
              Are you sure you want to delete the device &quot;{deleteConfirm.name}&quot;?
              This action cannot be undone and you will no longer be able to generate codes for this device.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleDelete(deleteConfirm.device_id)}
                disabled={deleting}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Delete Device'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MfaDeviceManager
